import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Package, AlertTriangle, CalendarClock, Percent, Tags, Scale } from "lucide-react";
import useScannerInput from "@/lib/useScannerInput";
import WeighLabelDialog from "@/components/scale/WeighLabelDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApp } from "@/context/AppContext";
import { ProductIcon, catColor, hasPhoto, furnitureSpec } from "@/lib/catalog";
import { daysUntil, expiryStatus, fmtNum, isLow, vals } from "@/lib/format";
import { BigButton, Empty, PageTitle } from "@/components/ui-kit";
import ProductFormDialog from "@/components/inventory/ProductFormDialog";
import BulkPrice from "@/components/inventory/BulkPrice";
import LabelsDialog from "@/components/inventory/LabelsDialog";
import DataGrid from "@/components/DataGrid";
import useIsDesktop from "@/lib/useIsDesktop";
import { VehicleFilter, vehicleMatch, vehicleText } from "@/components/inventory/VehicleFilter";

function ExpiryBadge({ p }) {
  const { t } = useApp();
  const st = expiryStatus(p);
  if (!st) return null;
  const cls = { expired: "bg-destructive text-white", soon: "bg-[#E67E22] text-white", ok: "bg-[#E3F6EC] text-[#1F9D55]" }[st];
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${cls}`} data-testid={`expiry-badge-${st}`}>{st === "expired" ? t("expired") : `${t("expires")} ${daysUntil(p.expiry)}${t("days_short")}`}</span>;
}

function ProductRow({ p, onOpen, lowDefault }) {
  const { t, money } = useApp();
  const low = isLow(p, lowDefault);
  return (
    <button type="button" onClick={() => onOpen(p)} data-testid="inventory-product-row" data-product-id={p.id}
      className="w-full rounded-2xl bg-white border border-border p-3 flex items-center gap-3 text-start hover:border-primary transition-colors">
      <span className="w-12 h-12 rounded-xl grid place-items-center text-white shrink-0 overflow-hidden" style={{ background: hasPhoto(p) ? "#eef2f6" : catColor(p.category) }}><ProductIcon product={p} /></span>
      <span className="flex-1 min-w-0">
        <span className="block font-bold truncate">{p.name}</span>
        <span className="flex gap-2 items-center flex-wrap text-sm text-muted-foreground">{t(`cat_${p.category}`)}{!!furnitureSpec(p) && <span dir="ltr" data-testid="inventory-spec">· {furnitureSpec(p)}</span>}{p.made_to_order && <span className="text-[11px] font-bold text-[#8E44AD]">{t("made_to_order")}{p.lead_days ? ` · ${p.lead_days}d` : ""}</span>} <ExpiryBadge p={p} /></span>
      </span>
      <span className="text-end shrink-0">
        <span className="block font-extrabold" data-testid="inventory-product-price">{money(p.price)}</span>
        <span className={`inline-block text-sm font-bold px-2 rounded-md ${low ? "bg-destructive/10 text-destructive" : "text-muted-foreground"}`} data-testid="inventory-product-stock">{fmtNum(p.stock)} {p.unit}{p.sold_by_weight && " · /kg"}</span>
      </span>
    </button>
  );
}

export default function Inventory() {
  const { view, t, can, dispatch } = useApp();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [labels, setLabels] = useState(false);
  const [weighLabels, setWeighLabels] = useState(false);
  const [params, setParams] = useSearchParams();
  const openCode = (code) => {
    const p = vals(view.products).find((x) => !x.deleted && x.barcode === code);
    if (p) setEditing(p);
    else if (can("inventory")) setEditing({ barcode: code });
  };
  useEffect(() => {
    const code = params.get("barcode");
    if (code) { openCode(code); setParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  useScannerInput(openCode, !editing && !labels && !weighLabels);
  const lowDefault = view.shop?.low_stock_default;
  const products = useMemo(() => vals(view.products).filter((p) => !p.deleted).sort((a, b) => a.name.localeCompare(b.name)), [view.products]);
  const isAuto = view.shop?.type === "autoparts";
  const [vehicle, setVehicle] = useState({ make: "", model: "" });
  const shown = products.filter((p) => (!isAuto || vehicleMatch(p, vehicle)) && `${p.name} ${p.barcode || ""} ${isAuto ? vehicleText(p) : ""}`.toLowerCase().includes(q.toLowerCase()));
  const low = products.filter((p) => isLow(p, lowDefault));
  const expiring = products.filter((p) => ["expired", "soon"].includes(expiryStatus(p))).sort((a, b) => a.expiry.localeCompare(b.expiry));
  const open = (p) => can("inventory") && setEditing(p);
  const isDesktop = useIsDesktop();
  const cols = [
    { key: "name", label: t("name") }, { key: "category", label: t("category"), get: (p) => t(`cat_${p.category}`) },
    ...(isAuto ? [{ key: "vehicle_make", label: t("vehicle"), get: (p) => `${p.vehicle_make || ""} ${p.vehicle_model || ""}`.trim() }, { key: "oem_ref", label: t("oem_ref") }] : []),
    ...(view.shop?.type === "furniture" ? [{ key: "spec", label: t("spec"), get: (p) => furnitureSpec(p) }, { key: "made_to_order", label: t("made_to_order"), get: (p) => (p.made_to_order ? `✓ ${p.lead_days ? p.lead_days + "d" : ""}` : "") }] : []),
    { key: "barcode", label: t("barcode") }, { key: "price", label: t("price"), align: "end" }, { key: "cost", label: t("cost"), align: "end" },
    { key: "stock", label: t("stock"), align: "end", sum: true, render: (p) => <span className={isLow(p, lowDefault) ? "text-destructive font-bold" : ""}>{fmtNum(p.stock)} {p.unit}</span> },
    { key: "expiry", label: t("expiry"), get: (p) => p.expiry || "" }, { key: "plu", label: "PLU", get: (p) => (p.sold_by_weight ? p.plu : "") },
  ];

  return (
    <div>
      <PageTitle testid="inventory-title" action={can("inventory") && <div className="flex gap-2"><BigButton variant="soft" onClick={() => setWeighLabels(true)} data-testid="inventory-weigh-label-btn"><Scale className="w-5 h-5" /><span className="hidden sm:inline">{t("weigh_label")}</span></BigButton><BigButton variant="soft" onClick={() => setLabels(true)} data-testid="inventory-labels-btn"><Tags className="w-5 h-5" /><span className="hidden sm:inline">{t("labels")}</span></BigButton><BigButton onClick={() => setEditing({})} data-testid="inventory-add-product-btn"><Plus className="w-5 h-5" /><span className="hidden sm:inline">{t("add_product")}</span></BigButton></div>}>
        {t("nav_stock")}
      </PageTitle>
      {(low.length > 0 || expiring.length > 0) && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-2xl bg-destructive/10 text-destructive p-3 font-bold flex items-center gap-2" data-testid="inventory-low-stock-alert"><AlertTriangle className="w-5 h-5" />{low.length} {t("low_stock")}</div>
          <div className="rounded-2xl bg-[#E67E22]/10 text-[#A85A12] p-3 font-bold flex items-center gap-2" data-testid="inventory-expiry-alert"><CalendarClock className="w-5 h-5" />{expiring.length} {t("expiring_soon")}</div>
        </div>
      )}
      <Tabs defaultValue="products">
        <TabsList className="w-full h-12 mb-4">
          <TabsTrigger value="products" className="flex-1 h-10 font-bold" data-testid="inventory-tab-products"><Package className="w-4 h-4 me-1" />{t("products")}</TabsTrigger>
          <TabsTrigger value="alerts" className="flex-1 h-10 font-bold" data-testid="inventory-tab-alerts"><AlertTriangle className="w-4 h-4 me-1" />{t("alerts")}</TabsTrigger>
          {can("inventory") && <TabsTrigger value="prices" className="flex-1 h-10 font-bold" data-testid="inventory-bulk-price-btn"><Percent className="w-4 h-4 me-1" />{t("prices")}</TabsTrigger>}
        </TabsList>
        <TabsContent value="products">
          {isAuto && <div className="mb-3"><VehicleFilter products={products} value={vehicle} onChange={setVehicle} testid="inventory-vehicle" /></div>}
          <div className="relative mb-3">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-5 h-5 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && q.trim() && !shown.length) { openCode(q.trim()); setQ(""); } }} placeholder={t("search_products")} data-testid="inventory-search-input"
              className="w-full h-14 rounded-2xl border border-border bg-white ps-11 pe-3 focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          {isDesktop ? <DataGrid columns={cols} rows={shown} onRowClick={open} onAdd={can("inventory") ? () => setEditing({}) : null} onDelete={can("inventory") ? (p) => window.confirm(t("confirm_delete")) && dispatch("product.delete", { id: p.id, name: p.name }) : null} name="products" testid="products-grid" defaultSort={{ key: "name", dir: 1 }} />
            : shown.length === 0 ? <Empty icon={Package} text={t("no_products")} testid="inventory-empty" /> : (
            <div className="grid md:grid-cols-2 gap-2">{shown.map((p) => <ProductRow key={p.id} p={p} onOpen={open} lowDefault={lowDefault} />)}</div>
          )}
        </TabsContent>
        <TabsContent value="alerts" className="space-y-5">
          <section>
            <h3 className="font-heading font-bold text-lg mb-2">{t("low_stock")}</h3>
            {low.length === 0 ? <p className="text-muted-foreground">{t("all_good")}</p> : <div className="grid md:grid-cols-2 gap-2">{low.map((p) => <ProductRow key={p.id} p={p} onOpen={open} lowDefault={lowDefault} />)}</div>}
          </section>
          <section>
            <h3 className="font-heading font-bold text-lg mb-2">{t("expiring_soon")}</h3>
            {expiring.length === 0 ? <p className="text-muted-foreground">{t("all_good")}</p> : <div className="grid md:grid-cols-2 gap-2">{expiring.map((p) => <ProductRow key={p.id} p={p} onOpen={open} lowDefault={lowDefault} />)}</div>}
          </section>
        </TabsContent>
        {can("inventory") && <TabsContent value="prices"><BulkPrice products={products} /></TabsContent>}
      </Tabs>
      <ProductFormDialog product={editing} onClose={() => setEditing(null)} />
      <LabelsDialog open={labels} onClose={() => setLabels(false)} products={products} />
      <WeighLabelDialog open={weighLabels} onClose={() => setWeighLabels(false)} products={products} />
    </div>
  );
}
